import React, { createContext, useContext, useReducer, useEffect, useRef, useState } from 'react';
import { useProfile } from './components/state/hooks';
import { getSetupChecklist, updateSetupChecklist, isLoggedIn } from './service';
import { isNewUserProfile } from './utils/userAccessHelper.js';
import { trackTourTransition } from './utils/productEventMappers';

// This file is used to manage the state of the each tour.
// Setup-checklist tasks (the 8-row onboarding state shown for school
// teachers) are hydrated from /users/setupChecklist on profile load and
// persisted back on every change via a debounced PATCH. localStorage is
// kept as a fast-cache backstop so the checklist renders instantly on
// reload while the backend roundtrip is in flight.
export const TourContext = createContext();

export const initialState = {
    showOnboardingComplete: false,
    showVerifyClassesPopup: false,
    showSessionBooking: false,
    // Dead as of the opaque-carousel fix (no reader; the carousel backdrop is
    // unconditionally opaque now). Retained so the jeddle_tour_state
    // localStorage snapshot shape stays stable for already-persisted values.
    teacherOnboardingOverlay: true,
    markingTourActive: false,
    newTaskTourActive: false,
    newTaskTourStep: 1,
    onboardingTasks: Array(8).fill(false),
    completedTours: {},
    draftTourActive: false,
    draftTourStep: 1,
    markingTourStep: 1,
    feedbackTourActive: false,
    feedbackTourStep: 1,
    // Simplified "wizard" tour for non-school teachers + students on the demo
    // "My Drafts" page. Separate flags so the school draft tour is untouched.
    wizardTourActive: false,
    wizardTourStep: 1,
    // 'teacher' | 'student' — set by App.jsx when launching the wizard so the
    // (teacher-view) demo mirror can pick the right submit-prompt copy + the
    // right plans modal. The demo hardcodes isTeacher=true, so it can't infer it.
    wizardAudience: 'teacher',
    showDraftFeedback: false,
    onboardingProgress: 0,
    hasLoadedUserPersistence: false,
};

export const MOBILE_TAKEOVER_STATE = {
    showOnboardingComplete: false,
    markingTourActive: false,
    draftTourActive: false,
    newTaskTourActive: false,
    feedbackTourActive: false,
    wizardTourActive: false,
    markingTourStep: 1,
    draftTourStep: 1,
    newTaskTourStep: 1,
    feedbackTourStep: 1,
    wizardTourStep: 1,
    showDraftFeedback: false,
};

export function tourReducer(state, action) {
    switch (action.type) {
        case 'SET_STATE':
            return { ...state, ...action.payload };
        case 'UPDATE_FIELD':
            const value = typeof action.value === 'function' ? action.value(state[action.field]) : action.value;
            return { ...state, [action.field]: value };
        case 'MARK_TOUR_COMPLETE': {
            // Map each explore-section tour to its index in onboardingTasks.
            // Personalise (0/1/2) is driven by popup submissions, not tours.
            // Item 7 (View class insights) has no tour and stays manual.
            const tourToTaskIndex = {
                markingTour: 3,
                draftTour: 4,
                newTaskTour: 5,
                feedbackTour: 6,
            };
            const idx = tourToTaskIndex[action.tourName];
            let nextTasks = state.onboardingTasks;
            if (idx != null && nextTasks[idx] !== true) {
                nextTasks = [...nextTasks];
                nextTasks[idx] = true;
            }
            return {
                ...state,
                completedTours: { ...state.completedTours, [action.tourName]: true },
                onboardingTasks: nextTasks,
            };
        }
        default:
            return state;
    }
}

const SAVE_DEBOUNCE_MS = 800;
const TASKS_LENGTH = 8;

// Setup Progress = share of the 8 checklist tasks done. Derived from the
// tasks every render (see `value` below) instead of trusting the stored
// onboardingProgress number: that number was only written while the
// checklist was mounted and was restored on reload only for new users who
// had NOT completed onboarding, so a finished teacher came back with 0 and
// the sidebar ring reappeared at 0% (JED-1275).
export const progressFromTasks = (tasks) => {
    if (!Array.isArray(tasks)) return 0;
    const done = tasks.slice(0, TASKS_LENGTH).filter(Boolean).length;
    return Math.round((done / TASKS_LENGTH) * 100);
};

const normaliseTasks = (raw) => {
    const out = Array(TASKS_LENGTH).fill(false);
    if (!Array.isArray(raw)) return out;
    for (let i = 0; i < TASKS_LENGTH; i++) {
        out[i] = Boolean(raw[i]);
    }
    return out;
};

// Checklist tasks only ever become done (nothing un-ticks one), so two views
// of the same checklist merge as a per-task OR without losing anything.
const unionTasks = (a, b) =>
    normaliseTasks(a).map((done, i) => done || Boolean(Array.isArray(b) && b[i]));

const NO_CHECKLIST_LOAD = { cacheComplete: false, incomplete: false };

export const TourProvider = ({ children }) => {
    const [state, dispatch] = useReducer(tourReducer, initialState);
    // Session-only flag: set true when a teacher explicitly RE-LAUNCHES the
    // onboarding from the Help Centre. Lets App.jsx's new-users-only render
    // gates open the surface for already-finished / pre-tour teachers without
    // re-persisting anything (it is deliberately NOT part of the reducer
    // `state`, so it never hits localStorage or the backend setupChecklist).
    // Reset on user switch below so it can't leak across sessions.
    const [tourRelaunchRequested, setTourRelaunchRequested] = useState(false);
    // Transient (NOT persisted, like the flag above): set true by the wizard's
    // Finish/Skip so App.jsx can run the end-of-wizard step (plans modal for
    // teachers + non-school students, or redirect to My Drafts for school
    // students). Reset on user switch + once App has handled it.
    const [wizardCompleted, setWizardCompleted] = useState(false);
    // Session-only: whether this user's server checklist has been read.
    //  - 'pending': not yet. onboardingTasks are an all-false placeholder or
    //    the localStorage cache, which may be stale (setup finished on
    //    another device), so the sidebar ring must not show them as current.
    //  - 'synced': the server checklist arrived, merged with what this
    //    device knew (see readSetupChecklist).
    //  - 'failed': the server read failed.
    // The placeholder is never treated as real: it is not cached, and the
    // ring stays hidden for it (see hasSyncedSetupChecklist below) (JED-1275).
    const [setupChecklistSync, setSetupChecklistSync] = useState('pending');
    // Session-only, set once when this user's checklist is hydrated
    // (JED-1275):
    //  - cacheComplete: the localStorage cache had every task done. Tasks
    //    only ever become done, so that cache cannot be stale; a partial one
    //    can be, and waits for the server.
    //  - incomplete: the profile said setup was still in progress. Leaving
    //    setup part-way sets hasCompletedOnboarding during the session, and
    //    the ring is the way back TourExitWarning promises, so this is read
    //    at load rather than live.
    const [setupChecklistLoad, setSetupChecklistLoad] = useState(NO_CHECKLIST_LOAD);
    // Transient exit-confirmation request: the destination path the user asked
    // to navigate to while the onboarding surface was active. TourGuard owns
    // the TourExitWarning dialog and consumes this — a non-null value shows
    // the warning; confirm tears onboarding down and navigates, cancel clears
    // it. Lives in context (not TourGuard-local state) so the sidebars can
    // raise the warning for explicit nav-item clicks, not just for history
    // escapes TourGuard intercepts itself. Never persisted.
    const [tourExitRequest, setTourExitRequest] = useState(null);
    // Gate useProfile on auth state so it doesn't fire a pre-auth fetch that
    // caches `null` and starves the userId-change effect of a transition.
    // We mirror App.jsx's pattern (useProfile(null, isAuthenticated)) — but
    // TourProvider sits above App in the tree, so we lift the auth check
    // into a localStorage-backed flag and poll it with a small interval +
    // storage-event subscription. Fast (<50ms), survives the first render.
    const [loggedIn, setLoggedIn] = useState(() => isLoggedIn());
    useEffect(() => {
        // localStorage updates inside the same tab don't fire `storage`,
        // so poll briefly to catch the post-exchange write in App.jsx.
        // Stop polling after MAX_TRIES * 100ms (~30s) to avoid leaking the
        // interval forever on public paths where the user never logs in
        // (password-reset, share-link, IDP redirect bounce). 30s covers a slow
        // in-tab OAuth exchange (the prior 10s could expire mid-handshake and
        // starve the userId effect, so onboarding never initialised) while still
        // bounding the work on truly-public pages. The storage event listener
        // stays armed for cross-tab sync.
        if (loggedIn) return undefined;
        const MAX_TRIES = 300;
        let tries = 0;
        const interval = setInterval(() => {
            if (isLoggedIn()) {
                setLoggedIn(true);
                clearInterval(interval);
                return;
            }
            tries += 1;
            if (tries >= MAX_TRIES) clearInterval(interval);
        }, 100);
        const onStorage = () => { if (isLoggedIn()) setLoggedIn(true); };
        window.addEventListener('storage', onStorage);
        return () => { clearInterval(interval); window.removeEventListener('storage', onStorage); };
    }, [loggedIn]);
    const { data: profile } = useProfile(null, loggedIn);
    const lastLoadedUserId = useRef(null);
    const lastSyncedTasksRef = useRef(null);
    const saveDebounceRef = useRef(null);
    // Whether this user's tasks changed in this session (a tour finished, a
    // popup saved): real progress, even while the server checklist is unknown.
    const tasksEditedRef = useRef(false);

    // Read the server checklist and merge in what this device knows. A stale
    // cache adds nothing to the server's tasks, while progress made here that
    // never reached the server (a reload before the debounced save, a failed
    // PATCH, a change made while the read was out) is kept, and the save
    // effect writes the union back (JED-1275).
    const readSetupChecklist = (userId, isActive = () => true) => {
        const current = () => isActive() && lastLoadedUserId.current === userId;
        // A late failure never undoes a successful read.
        const markReadFailed = () => setSetupChecklistSync((prev) => (prev === 'synced' ? prev : 'failed'));
        return getSetupChecklist()
            .then((remote) => {
                if (!current()) return;
                // 401 resolves undefined (service.js clears the session).
                if (!remote || typeof remote !== 'object') {
                    markReadFailed();
                    return;
                }
                // A 200 without tasks is a user with no checklist row yet:
                // nothing done on the server.
                const remoteTasks = Array.isArray(remote.tasks) ? normaliseTasks(remote.tasks) : Array(TASKS_LENGTH).fill(false);
                lastSyncedTasksRef.current = remoteTasks;
                dispatch({ type: 'UPDATE_FIELD', field: 'onboardingTasks', value: (local) => unionTasks(remoteTasks, local) });
                setSetupChecklistSync('synced');
            })
            .catch(() => {
                // Network failure — keep the localStorage fallback. The next
                // change reads again before writing (see the save effect).
                if (current()) markReadFailed();
            });
    };

    // Load user data when userId changes
    useEffect(() => {
        const currentUserId = profile?.userId || null;

        // Any user transition invalidates an in-flight debounced save — the
        // pending snapshot belongs to the PREVIOUS user, but the PATCH would
        // run under the new session's token and write the old user's tasks
        // into the new user's setupChecklist row.
        const cancelPendingSave = () => {
            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
                saveDebounceRef.current = null;
            }
        };

        // If we have no user, reset immediately
        if (!currentUserId) {
            if (lastLoadedUserId.current !== null) {
                cancelPendingSave();
                dispatch({ type: 'SET_STATE', payload: { ...initialState, hasLoadedUserPersistence: false } });
                setTourRelaunchRequested(false);
                setWizardCompleted(false);
                setTourExitRequest(null);
                setSetupChecklistSync('pending');
                setSetupChecklistLoad(NO_CHECKLIST_LOAD);
                lastLoadedUserId.current = null;
                lastSyncedTasksRef.current = null;
                tasksEditedRef.current = false;
            }
            return;
        }

        // If user changed, reset and load
        if (lastLoadedUserId.current !== currentUserId) {
            cancelPendingSave();
            // CRITICAL: Reset state immediately so components don't see previous user's data
            // Set hasLoadedUserPersistence to false to block logic that depends on loaded data
            dispatch({ type: 'SET_STATE', payload: { ...initialState, hasLoadedUserPersistence: false } });
            setTourRelaunchRequested(false);
            setWizardCompleted(false);
            setTourExitRequest(null);

            const savedTasks = localStorage.getItem(`jeddle_onboarding_tasks_${currentUserId}`);
            const savedTours = localStorage.getItem(`jeddle_completed_tours_${currentUserId}`);
            const savedTourState = localStorage.getItem(`jeddle_tour_state_${currentUserId}`);
            // Hydrate from localStorage first so the UI renders instantly,
            // then fetch the server state and merge (see readSetupChecklist).
            const cachedTasks = savedTasks ? normaliseTasks(JSON.parse(savedTasks)) : Array(TASKS_LENGTH).fill(false);
            // Shown at once, but trusted as current only once the server has
            // answered (or when it is complete: see setupChecklistLoad).
            setSetupChecklistSync('pending');
            setSetupChecklistLoad({
                cacheComplete: cachedTasks.every(Boolean),
                incomplete: !profile?.hasCompletedOnboarding,
            });
            tasksEditedRef.current = false;
            dispatch({
                type: 'SET_STATE',
                payload: {
                    ...initialState,
                    // Restore the persisted tour snapshot (active tour + step +
                    // showOnboardingComplete) so a hard refresh mid-tour lands
                    // the user back exactly where they were. ALL four tours are
                    // restored symmetrically — previously newTaskTourActive was
                    // force-reset to false here, which silently orphaned the
                    // new-task tour on refresh (overlay gone) while the other
                    // three survived. TourGuard's immediate-redirect (see its
                    // effect) keeps the URL aligned with the restored tour.
                    //
                    // GATED ON "genuinely mid-first-run onboarding" =
                    // isNewUserProfile && !hasCompletedOnboarding. Only that
                    // audience legitimately has a persistent in-progress tour;
                    // for everyone else the only way into the surface is the
                    // in-memory Help-Centre relaunch, which is intentionally
                    // transient (tourRelaunchRequested is not persisted).
                    //
                    // Both exclusions matter:
                    //  - returning teacher (no createdAt) → isNewUserProfile false
                    //  - FINISHED new-era teacher (createdAt present but
                    //    hasCompletedOnboarding true) → excluded by the
                    //    !hasCompletedOnboarding check. Their relaunch writes
                    //    showOnboardingComplete into jeddle_tour_state via the
                    //    persistence effect; without this guard a refresh would
                    //    re-open/redirect them into onboarding with no fresh
                    //    click, or (when the surface is hidden) reactivate
                    //    TourGuard and strand them on /tasks with nav blocked.
                    // New users still mid-onboarding keep full refresh
                    // restoration (the case we actually want to preserve).
                    ...((isNewUserProfile(profile) && !profile?.hasCompletedOnboarding && savedTourState) ? JSON.parse(savedTourState) : {}),
                    onboardingTasks: cachedTasks,
                    completedTours: savedTours ? JSON.parse(savedTours) : {},
                    hasLoadedUserPersistence: true,
                }
            });
            lastSyncedTasksRef.current = cachedTasks;
            lastLoadedUserId.current = currentUserId;

            let cancelled = false;
            readSetupChecklist(currentUserId, () => !cancelled);

            return () => {
                cancelled = true;
            };
        }
    }, [profile?.userId]);

    // Helper to ensure we only save if the state matches the current user
    const isReadyToSave = profile?.userId && lastLoadedUserId.current === profile.userId && state.hasLoadedUserPersistence;

    // Save tasks to localStorage (instant) and backend (debounced)
    useEffect(() => {
        if (!isReadyToSave) return;
        // Skip the network write-back when nothing actually changed since the last
        // sync — avoids a redundant PATCH on the initial localStorage hydration.
        // Hydration and server reads set lastSyncedTasksRef to the tasks they
        // load, so any other difference is a change made in this session.
        const lastSynced = lastSyncedTasksRef.current;
        const unchanged = lastSynced && lastSynced.every((v, i) => v === state.onboardingTasks[i]);
        if (!unchanged) tasksEditedRef.current = true;
        // Cache only real tasks: the server's, or ones changed here, at once,
        // so a reload before the debounced save (or after a failed read) keeps
        // them. Caching the all-false placeholder before the server answered
        // made a reload (or a failed read) show a finished teacher 0% (JED-1275).
        if (setupChecklistSync === 'synced' || tasksEditedRef.current) {
            localStorage.setItem(`jeddle_onboarding_tasks_${profile.userId}`, JSON.stringify(state.onboardingTasks));
        }
        if (unchanged) return;

        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
        }
        const snapshot = [...state.onboardingTasks];
        const userId = profile.userId;
        saveDebounceRef.current = setTimeout(() => {
            // The JED-1275 backend ORs a PATCH's done tasks into the stored
            // row, but an older backend replaces the whole row, so writing
            // tasks built on a checklist the server never sent would erase
            // progress saved elsewhere there. A read still out merges these
            // tasks when it lands; after a failed read, read again, and the
            // 'synced' pass writes the merged tasks.
            if (setupChecklistSync !== 'synced') {
                if (setupChecklistSync === 'failed') readSetupChecklist(userId);
                return;
            }
            updateSetupChecklist(snapshot)
                .then(() => {
                    lastSyncedTasksRef.current = snapshot;
                })
                .catch((err) => {
                    // Network failure — leave lastSyncedTasksRef alone so the next
                    // change retries. localStorage already has the latest. Log it
                    // so a teacher whose every PATCH silently fails (losing
                    // checklist progress on logout) is at least diagnosable.
                    // eslint-disable-next-line no-console
                    console.error('Failed to save setup checklist', err);
                });
        }, SAVE_DEBOUNCE_MS);
    }, [state.onboardingTasks, profile?.userId, isReadyToSave, setupChecklistSync]);

    useEffect(() => {
        return () => {
            if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        };
    }, []);

    // Save tours
    useEffect(() => {
        if (isReadyToSave) {
            localStorage.setItem(`jeddle_completed_tours_${profile.userId}`, JSON.stringify(state.completedTours));
        }
    }, [state.completedTours, profile?.userId, isReadyToSave]);

    // Save rest of the tour states
    useEffect(() => {
        if (isReadyToSave) {
            const {
                hasLoadedUserPersistence,
                onboardingTasks,
                completedTours,
                // The simplified wizard is a TRANSIENT in-session flow (like
                // tourRelaunchRequested / wizardCompleted) — never persist or
                // rehydrate it. Restoring wizardTourActive on a mid-wizard
                // refresh would click-lock the re-shown welcome carousel and
                // desync App's (non-persisted) simpleAudience. A refresh simply
                // restarts the simple flow from the auto-show effect.
                wizardTourActive,
                wizardTourStep,
                wizardAudience,
                ...stateToSave
            } = state;
            localStorage.setItem(`jeddle_tour_state_${profile.userId}`, JSON.stringify(stateToSave));
        }
    }, [
        state.showOnboardingComplete,
        state.showVerifyClassesPopup,
        state.showSessionBooking,
        state.teacherOnboardingOverlay,
        state.markingTourActive,
        state.newTaskTourActive,
        state.draftTourActive,
        state.feedbackTourActive,
        state.newTaskTourStep,
        state.draftTourStep,
        state.markingTourStep,
        state.feedbackTourStep,
        state.showDraftFeedback,
        state.onboardingProgress,
        profile?.userId,
        isReadyToSave
    ]);

    // Product events: a step event per tour step shown, and 'left' when a tour
    // stops without Finish/Skip (those are recorded by TourTooltip).
    const tourEventRef = useRef({ tour: null, step: null });
    useEffect(() => {
        tourEventRef.current = trackTourTransition(tourEventRef.current, state);
    }, [
        state.markingTourActive, state.draftTourActive, state.newTaskTourActive,
        state.feedbackTourActive, state.wizardTourActive, state.markingTourStep,
        state.draftTourStep, state.newTaskTourStep, state.feedbackTourStep,
        state.wizardTourStep,
    ]);

    const updateField = (field, value) => dispatch({ type: 'UPDATE_FIELD', field, value });

    const value = {
        ...state,
        // Always the tasks' real completion (see progressFromTasks).
        onboardingProgress: progressFromTasks(state.onboardingTasks),

        // Generic setter
        updateField,

        // Specific setters (matching legacy names for easier refactor)
        setMarkingTourActive: (val) => updateField('markingTourActive', val),
        setNewTaskTourActive: (val) => updateField('newTaskTourActive', val),
        setDraftTourActive: (val) => updateField('draftTourActive', val),
        setFeedbackTourActive: (val) => updateField('feedbackTourActive', val),

        setNewTaskTourStep: (val) => updateField('newTaskTourStep', val),
        setDraftTourStep: (val) => updateField('draftTourStep', val),
        setMarkingTourStep: (val) => updateField('markingTourStep', val),
        setFeedbackTourStep: (val) => updateField('feedbackTourStep', val),
        setWizardTourActive: (val) => updateField('wizardTourActive', val),
        setWizardTourStep: (val) => updateField('wizardTourStep', val),
        setWizardAudience: (val) => updateField('wizardAudience', val),
        setShowOnboardingComplete: (val) => updateField('showOnboardingComplete', val),
        setShowVerifyClassesPopup: (val) => updateField('showVerifyClassesPopup', val),
        setShowSessionBooking: (val) => updateField('showSessionBooking', val),
        setTeacherOnboardingOverlay: (val) => updateField('teacherOnboardingOverlay', val),
        setShowDraftFeedback: (val) => updateField('showDraftFeedback', val),
        // Stores a number nothing shows: the ring reads onboardingProgress
        // above, which is always progressFromTasks. Kept for existing callers;
        // to move the ring, tick a task (setOnboardingTasks).
        setOnboardingProgress: (val) => updateField('onboardingProgress', val),

        setOnboardingTasks: (val) => updateField('onboardingTasks', val),
        setCompletedTours: (val) => updateField('completedTours', val),

        markTourAsComplete: (tourName) => dispatch({ type: 'MARK_TOUR_COMPLETE', tourName }),

        // ATOMICALLY activate exactly one feature tour at step 1, deactivating
        // the other three and resetting every step. Single dispatch so there's
        // no window where two *TourActive flags are true (the four-boolean shape
        // otherwise lets a stale flag from a prior tour leak — e.g. handleBack
        // would then decrement an orphaned tour's step). tourName is one of
        // 'marking' | 'draft' | 'newTask' | 'feedback'.
        launchTour: (tourName) => dispatch({
            type: 'SET_STATE',
            payload: {
                markingTourActive: tourName === 'marking',
                draftTourActive: tourName === 'draft',
                newTaskTourActive: tourName === 'newTask',
                feedbackTourActive: tourName === 'feedback',
                wizardTourActive: tourName === 'wizard',
                markingTourStep: 1,
                draftTourStep: 1,
                newTaskTourStep: 1,
                feedbackTourStep: 1,
                wizardTourStep: 1,
            },
        }),

        // ATOMICALLY end whichever tour is active: clear all four flags + reset
        // all steps in one dispatch. Replaces the 8-line manual reset that was
        // duplicated in TourTooltip's finish + skip-all handlers.
        // Also force-close the draft-feedback modal: it is gated on
        // showDraftFeedback (App.jsx) and a Skip All / finish must never leave
        // the "Customise your draft feedback" overlay stranded on top of the
        // checklist (JED-1088).
        finishCurrentTour: () => dispatch({
            type: 'SET_STATE',
            payload: {
                markingTourActive: false,
                draftTourActive: false,
                newTaskTourActive: false,
                feedbackTourActive: false,
                wizardTourActive: false,
                markingTourStep: 1,
                draftTourStep: 1,
                newTaskTourStep: 1,
                feedbackTourStep: 1,
                wizardTourStep: 1,
                showDraftFeedback: false,
            },
        }),

        // Mobile has no desktop-tour anchors or checklist. Clear the feature
        // tour AND its checklist guard in one reducer transaction so TourGuard
        // never observes a half-cleared state and intercepts mobile navigation.
        finishMobileTakeover: () => {
            // A leave-warning requested just before the responsive takeover is
            // another TourGuard surface; do not strand that modal over mobile.
            setTourExitRequest(null);
            dispatch({
                type: 'SET_STATE',
                payload: MOBILE_TAKEOVER_STATE,
            });
        },

        // Help-Centre re-launch flag (session-only, see declaration above).
        tourRelaunchRequested,
        setTourRelaunchRequested,
        // Wizard completion signal (session-only, see declaration above).
        wizardCompleted,
        setWizardCompleted,
        // Whether the Setup Progress ring may show (session-only, see the
        // setupChecklistSync and setupChecklistLoad declarations above): only
        // for tasks known to be current — the server's, or a complete cache.
        // A partial cache waits for the server. When the server read failed,
        // a teacher whose setup was still in progress at load keeps the ring
        // (even after leaving setup part-way), as the way back into the
        // checklist that TourExitWarning promises; one who had completed
        // onboarding gets none.
        hasSyncedSetupChecklist:
            setupChecklistSync === 'synced' ||
            setupChecklistLoad.cacheComplete ||
            (setupChecklistSync === 'failed' && setupChecklistLoad.incomplete),
        // Exit-confirmation request (session-only, see declaration above).
        // requestTourExit(path) → TourGuard shows TourExitWarning for `path`.
        tourExitRequest,
        requestTourExit: setTourExitRequest,
        clearTourExitRequest: () => setTourExitRequest(null),

        // Special reset or bulk set
        setTourState: (payload) => dispatch({ type: 'SET_STATE', payload }),
    };

    return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within a TourProvider');
    }
    return context;
};
